/**
 * Minimal JWT reading.
 *
 * The token's claims are used only to keep the interface honest — hiding a
 * screen the account cannot use, and signing out before an expired token
 * produces a wall of failed requests. Every authorisation decision that
 * matters is still made by the API; a tampered token simply fails there.
 */

export interface JwtClaims {
  sub?: string
  id?: number
  name?: string
  email?: string
  phone?: string
  role?: string
  provider?: string
  enabled?: boolean
  /** Seconds since the epoch, per the JWT spec. */
  exp?: number
  iat?: number
}

function decodeBase64Url(segment: string): string | null {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const binary = atob(padded)
    // atob yields Latin-1; re-decode as UTF-8 so non-ASCII names survive.
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  } catch {
    return null
  }
}

export function decodeJwt(token: string): JwtClaims | null {
  const segments = token.split('.')
  if (segments.length !== 3) return null

  const payload = decodeBase64Url(segments[1])
  if (payload === null) return null

  try {
    const parsed: unknown = JSON.parse(payload)
    if (typeof parsed !== 'object' || parsed === null) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * A token counts as expired slightly before its real deadline, so a request
 * cannot leave with a token that dies in flight.
 */
export function isExpired(claims: JwtClaims | null, skewSeconds = 30): boolean {
  if (!claims || typeof claims.exp !== 'number') return false
  return claims.exp * 1000 <= Date.now() + skewSeconds * 1000
}

export function expiresAt(claims: JwtClaims | null): number | null {
  return claims && typeof claims.exp === 'number' ? claims.exp * 1000 : null
}
